import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Loader2, Check, Lock, RotateCcw, FolderTree } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { MODULE_META, ModuleKey } from '../lib/moduleMeta'
import { AccessLevel, ALL_LEVELS, LEVEL_CHIP, levelLabelKey } from '../lib/levelMeta'

const ACTIONS = ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'APPROVE', 'EXPORT', 'ADMIN'] as const

interface Props {
  module: ModuleKey
  level: AccessLevel
  overridden: boolean
  custom: boolean
  actions: string[]
  pending?: boolean
  onSetLevel: (level: AccessLevel) => void
  onSetActions: (actions: string[]) => void
  onReset: () => void
  onDrilldown?: () => void
}

const ModuleLevelCell: React.FC<Props> = ({
  module, level, overridden, custom, actions, pending, onSetLevel, onSetActions, onReset, onDrilldown,
}) => {
  const { t } = useTranslation()
  const meta = MODULE_META.find((m) => m.key === module)
  const hasDrilldown = meta?.hasDrilldown ?? false

  const [customOpen, setCustomOpen] = useState(false)
  const [draftActions, setDraftActions] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)

  const pillLabel = custom ? t('Members_level_custom', 'Custom') : t(levelLabelKey(level))

  const openCustom = () => {
    setDraftActions(new Set(actions))
    setSaving(false)
    setCustomOpen(true)
  }

  const toggleAction = (a: string) => setDraftActions((prev) => {
    const next = new Set(prev)
    next.has(a) ? next.delete(a) : next.add(a)
    return next
  })

  const handleSaveCustom = () => {
    setSaving(true)
    onSetActions([...draftActions])
    setCustomOpen(false)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={pending}>
          <button
            type="button"
            disabled={pending}
            title={actions.join(' · ')}
            aria-label={`${module}: ${pillLabel}`}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors',
              LEVEL_CHIP[level],
              pending ? 'opacity-60 cursor-wait' : 'cursor-pointer',
            )}
          >
            {overridden && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />}
            {pillLabel}
            {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronDown className="h-3 w-3 opacity-60" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[160px]">
          {ALL_LEVELS.map((lvl) => (
            <DropdownMenuItem key={lvl} onSelect={() => onSetLevel(lvl)} className="text-sm">
              <Check className={cn('h-4 w-4', overridden && !custom && lvl === level ? 'opacity-100' : 'opacity-0')} />
              {t(levelLabelKey(lvl))}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem onSelect={openCustom} className="text-sm">
            <Check className={cn('h-4 w-4', custom ? 'opacity-100' : 'opacity-0')} />
            {t('Members_custom_item', 'Custom…')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onReset} disabled={!overridden} className="text-sm">
            <RotateCcw className="h-4 w-4" />
            {t('Members_cell_reset')}
          </DropdownMenuItem>
          {hasDrilldown && module === 'DOC_CONTROL' && onDrilldown ? (
            <DropdownMenuItem onSelect={onDrilldown} className="text-sm">
              <FolderTree className="h-4 w-4" />
              {t('Members_drilldown_folders')}
            </DropdownMenuItem>
          ) : hasDrilldown && (
            <DropdownMenuItem disabled className="text-sm text-muted-foreground">
              <Lock className="h-4 w-4" />
              {t('Members_drilldown_soon')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Members_custom_title', 'Custom permissions')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {ACTIONS.map((a) => (
              <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={draftActions.has(a)} onCheckedChange={() => toggleAction(a)} />
                {t('perm_action_' + a, a)}
              </label>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setCustomOpen(false)}>{t('cancel', 'Cancel')}</Button>
            <Button onClick={handleSaveCustom} disabled={pending || saving}>
              {(pending || saving) && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('save', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ModuleLevelCell
